import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { CompanySecurityRolePreviewOutDTO, CompanyModulesEnum } from '@api/ringil-api.v2';
import { MultiSelect } from 'primereact/multiselect';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';
import { DataTable } from 'primereact/datatable';

import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { HeaderWithButtonText, RowWithSpaceBetween } from '@components/styles.tsx';
import { Clickable } from '@components/ringil3/styles.tsx';
import { RingilDialog } from '@components/obsolete/RingilDialog/RingilDialog.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import InputPickPermissions from '@components/obsolete/Form/InputPickPermissions/InputPickPermissions.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import SvgCheck from '@icons/check-square.svg?react';
import SecurityUsersDialog from '@components/admin/security/SecurityUsersDialog';

interface ComponentProps {}

const SecurityRoles: React.FC<ComponentProps> = () => {
  const { adminGetSecurityRoles, adminCreateSecurityRole, adminUpdateSecurityRole } = useContext(ApiContext);
  const [data, setData] = useState<CompanySecurityRolePreviewOutDTO[]>();
  const [editable, setEditable] = useState<CompanySecurityRolePreviewOutDTO>();
  const [usersDialogRoleId, setUsersDialogRoleId] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { tr } = useTranslationLgs();

  useEffect(() => {
    const ac = new AbortController();
    abortControllerRef.current = ac;

    load(abortControllerRef.current);

    return () => {
      abortControllerRef.current.abort();
    };
  }, []);

  const load = (ac: AbortController) => {
    adminGetSecurityRoles(d => {
      if (ac.signal.aborted) {
        return;
      }
      setData(d);
    });
  };

  const save = () => {
    if (isNotNullOrUndefined(editable)) {
      const roleData = {
        name: editable.name,
        permissionTypes: editable.permissionTypes,
        isPrivate: editable.isPrivate ?? false,
        isOnlyInternal: editable.isOnlyInternal ?? false,
        descriptionKey: editable.descriptionKey,
        companyModules: editable.companyModules ?? [],
      };

      const onSuccess = () => {
        setEditable(null);
        load(abortControllerRef.current);
      };

      if (isNullOrUndefined(editable.id)) {
        adminCreateSecurityRole(roleData, onSuccess);
      } else {
        adminUpdateSecurityRole(editable.id, roleData, onSuccess);
      }
    }
  };

  if (isNullOrUndefined(data)) return <>Loading...</>;

  return (
    <>
      <RowWithSpaceBetween>
        <HeaderWithButtonText>Security roles</HeaderWithButtonText>
        <Button
          label="Add security role"
          iconPos="left"
          icon="icon pi pi-pencil"
          className="p-button-outlined"
          onClick={() =>
            setEditable({
              name: '',
              isGlobal: true,
              id: null,
              permissionTypes: [],
              isPrivate: false,
              isOnlyInternal: false,
              userCount: 0,
              companyModules: Object.values(CompanyModulesEnum),
            })
          }
        ></Button>
      </RowWithSpaceBetween>

      {/*// @ts-ignore*/}
      <DataTable value={data} emptyMessage={tr(`global.noRecordFound`, `Nebyly nalezeny žádné záznamy`)}>
        <Column header="Name" body={(row: CompanySecurityRolePreviewOutDTO) =>
          <div>
            <div>{row.name}</div>
            <div className="text-sm text-content-secondary break-words w-full">{row.descriptionKey}</div>
          </div>
        } />
        <Column header="IsGlobal" body={(row: CompanySecurityRolePreviewOutDTO) => <>{row.isGlobal ? 'G' : 'C'}</>}
                style={{ width: '10%' }} />
        <Column
          header="Permissions"
          body={(row: CompanySecurityRolePreviewOutDTO) => <Clickable onClick={() => setEditable(row)}>{row.permissionTypes.length}</Clickable>}
          style={{ width: '10%' }}
        />
        <Column
          header={tr('AdminSecurityRoles.modules', 'Modules')}
          body={(row: CompanySecurityRolePreviewOutDTO) =>
            (row.companyModules ?? []).length === Object.values(CompanyModulesEnum).length ? (
              <span>{tr('AdminSecurityRoles.allModules', 'All')}</span>
            ) : (
              <span className="text-sm break-words">{(row.companyModules ?? []).map(m => tr(`InputModuleType.module${m}`, m)).join(', ')}</span>
            )
          }
          style={{ width: '15%' }}
        />
        <Column
          header={tr('AdminSecurityRoles.tableUsers', 'Users')}
          body={(row: CompanySecurityRolePreviewOutDTO) =>
            row.id ? (
              <Clickable onClick={() => setUsersDialogRoleId(row.id)}>{row.userCount}</Clickable>
            ) : (
              <span>-</span>
            )
          }
          style={{ width: '10%' }}
        />
        <Column
          header={tr('AdminSecurityRoles.tableInternalOnly', 'Internal only')}
          body={(row: CompanySecurityRolePreviewOutDTO) =>
            row.isOnlyInternal
              ? <span className={'text-complementary-success'}><SvgCheck /></span>
              : '-'
          }
          style={{ width: '10%' }}
        />
        <Column
          header={tr('AdminSecurityRoles.tablePrivate', 'Private')}
          body={(row: CompanySecurityRolePreviewOutDTO) =>
            row.isPrivate
              ? <span className={'text-complementary-success'}><SvgCheck /></span>
              : '-'
          }
          style={{ width: '10%' }}
        />
      </DataTable>
      <RingilDialog
        visible={isNotNullOrUndefined(editable)}
        onHide={() => setEditable(null)}
        onSave={() => save()}
        headerText={'Create/Edit security role'} width={'80vw'}
      >
        {isNotNullOrUndefined(editable) && (
          <>
            <HorizontalFieldValue
              label={tr('AdminSecurityRoles.name', 'SecurityGroup name')}
              value={<InputText value={editable.name} onChange={e => setEditable({ ...editable, name: e.target.value })} />}
            />
            <HorizontalFieldValue
              label={tr('AdminSecurityRoles.description', 'SecurityGroup description')}
              value={<InputText value={editable.descriptionKey}
                                onChange={e => setEditable({ ...editable, descriptionKey: e.target.value })} />}
            />
            <HorizontalFieldValue
              label={tr('AdminSecurityRoles.internalOnly', 'SecurityGroup only for internal use')}
              value={
                <RingilInputSwitch
                  value={editable.isOnlyInternal}
                  onChange={v => setEditable({ ...editable, isOnlyInternal: v })}
                  label={tr('AdminSecurityRoles.internalOnly', 'SecurityGroup only for internal use')}
                />
              }
            />
            <HorizontalFieldValue
              label={tr('AdminSecurityRoles.private', 'SecurityGroup private')}
              value={
                <RingilInputSwitch
                  value={editable.isPrivate}
                  onChange={v => setEditable({ ...editable, isPrivate: v })}
                  label={tr('AdminSecurityRoles.private', 'SecurityGroup private')}
                />
              }
            />
            <HorizontalFieldValue
              label={tr('AdminSecurityRoles.modules', 'Modules')}
              value={
                <MultiSelect
                  value={editable.companyModules?? []}
                  options={Object.values(CompanyModulesEnum)}
                  onChange={e => setEditable({ ...editable, companyModules: e.value })}
                  itemTemplate={(v: CompanyModulesEnum) => <div>{tr(`InputModuleType.module${v}`, v)}</div>}
                  selectedItemTemplate={(v: CompanyModulesEnum) => (v ? <span className="mr-2">{tr(`InputModuleType.module${v}`, v)}</span> : null)}
                />
              }
            />
            <HorizontalFieldValue
              label="Permissions"
              value={<InputPickPermissions value={editable.permissionTypes} onChange={v => setEditable({ ...editable, permissionTypes: v })} />}
            />
          </>
        )}
      </RingilDialog>
      <SecurityUsersDialog
        visible={usersDialogRoleId !== null}
        onHide={() => setUsersDialogRoleId(null)}
        filter={usersDialogRoleId !== null ? { roleId: usersDialogRoleId } : null}
        title={tr('AdminSecurityRoles.usersForRole', 'Users for role')}
      />
    </>
  );
};

export default SecurityRoles;
